import type { DeliveryNoteResponse } from '@palaccount/contracts';
import { CheckCheck, Pencil, Receipt, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Qty } from '@/components/amount';
import {
  BulkExportButton,
  BulkPdfButton,
  BulkPostButton,
  DocumentListPage,
  DocumentPdfButton,
  documentLocale,
  normalisePaged,
  type DocumentListConfig,
} from '@/components/document-list';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause, throwApiError } from '@/lib/server-messages';

import {
  DELIVERIES_KEY,
  DELIVERIES_SEARCHABLE,
  fetchDeliveriesPage,
  useCancelDelivery,
  useDeliveries,
  useDelivery,
  useDeliveryStats,
  usePostDelivery,
} from './deliveries-api';
import { ORDERS_KEY } from './quotes-api';
import { deliverySteps } from './sales-steppers';
import { SalesTabs } from './sales-tabs';

const STATUSES: DeliveryNoteResponse['status'][] = ['draft', 'posted', 'cancelled'];
const TONES = { draft: 'draft', posted: 'posted', cancelled: 'cancelled' } as const;

type DeliveryStats = ReturnType<typeof useDeliveryStats>['data'] & object;

const trimQty = (qty: string) => qty.replace(/\.?0+$/, '');

/**
 * SAL-03's list on the shared list-and-detail page (wave 3B). A delivery
 * note counts things, so the tiles are counts and the card carries no
 * money. The selected note sits at Delivery in the order-to-cash chain with
 * its order linked (SAL-02 §5c's pill, reversed), says how much of the order
 * has left, and offers the acts: confirm a draft (BR-SAL-03-03), print it,
 * make the invoice from the ORDER once the goods have gone (SAL-02's Create
 * invoice, reached on the orders page), cancel a confirmed one (BR-SAL-03-07,
 * reason required — GEN-72).
 */
export function DeliveriesListPage() {
  const { t, i18n } = useTranslation();
  const locale = documentLocale(i18n.language);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const postDelivery = usePostDelivery(selectedId);
  const cancelDelivery = useCancelDelivery(selectedId);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.approve');
      setError(localizeServerCause(t, thrown, 'doclist.actFailed'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (status: DeliveryNoteResponse['status']) => t(`common.status.${status}`);
  const chip = (note: DeliveryNoteResponse) => <StatusChip status={note.status} tones={TONES} ns="common" />;
  const exportColumns = [
    { header: t('dn.colNo'), value: (n: DeliveryNoteResponse) => n.code },
    { header: t('common.date'), value: (n: DeliveryNoteResponse) => n.deliveryDate, kind: 'date' as const },
    { header: t('sales.colCustomer'), value: (n: DeliveryNoteResponse) => n.partnerName },
    { header: t('dn.colOrder'), value: (n: DeliveryNoteResponse) => n.salesOrderCode ?? '' },
    { header: t('dn.colWarehouse'), value: (n: DeliveryNoteResponse) => n.warehouseName },
    { header: t('dn.colOrdered'), value: (n: DeliveryNoteResponse) => n.totalOrdered, kind: 'number' as const },
    { header: t('dn.colDelivered'), value: (n: DeliveryNoteResponse) => n.totalDelivered, kind: 'number' as const },
    { header: t('sales.colStatus'), value: (n: DeliveryNoteResponse) => statusLabel(n.status) },
  ];
  const pdfRequest = (note: DeliveryNoteResponse) => ({ kind: 'delivery_note' as const, locale, subjectId: note.id });
  const toFilters = (filters: { status: DeliveryNoteResponse['status'] | ''; dateFrom: string; dateTo: string }) => ({
    status: filters.status || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
  });

  const config: DocumentListConfig<DeliveryNoteResponse, DeliveryNoteResponse['status'], DeliveryStats> = {
    titleKey: 'dn.title',
    tabs: <SalesTabs />,
    newAction: { to: '/sales/deliveries/new', labelKey: 'common.new', permission: 'sales.create', reasonKey: 'perm.cannot.createDeliveries' },
    statuses: STATUSES,
    statusLabel,
    /* The box appears with the contract's `q` (wave 2B) — see DELIVERIES_SEARCHABLE. */
    search: DELIVERIES_SEARCHABLE ? { labelKey: 'doclist.searchLabel', placeholderKey: 'doclist.searchPlaceholder' } : undefined,
    dateRange: true,
    useList: (filters, page, pageSize) => {
      const query = useDeliveries(toFilters(filters), page, pageSize);
      return { data: normalisePaged(query.data, 'deliveries'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => useDelivery(id ?? undefined),
    kpis: {
      useStats: () => {
        const stats = useDeliveryStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'drafts', labelKey: 'dn.tileDrafts', format: 'count', value: (s) => s.draftsToPost },
        { key: 'week', labelKey: 'dn.tileThisWeek', format: 'count', value: (s) => s.deliveredThisWeek, tone: 'pos' },
        { key: 'awaiting', labelKey: 'dn.tileAwaiting', format: 'count', value: (s) => s.ordersAwaitingDelivery, tone: 'warn' },
        { key: 'partial', labelKey: 'dn.tilePartial', format: 'count', value: (s) => s.partiallyDelivered },
      ],
    },
    card: (note) => ({
      code: note.code,
      chip: chip(note),
      title: note.partnerName,
      meta: (
        <bdi dir="ltr">
          {note.deliveryDate} · {trimQty(note.totalDelivered)} / {trimQty(note.totalOrdered)}
        </bdi>
      ),
    }),
    columns: { titleKey: 'sales.colCustomer', metaKey: 'dn.colDelivered', statusKey: 'sales.colStatus' },
    emptyKey: 'dn.empty',
    selectOneKey: 'dn.selectOne',
    notice: error ? (
      <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    ) : null,
    export: {
      filename: 'delivery-notes',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) => fetchDeliveriesPage(toFilters(filters), page, pageSize),
    },
    bulkActions: (rows, clear) => (
      <>
        <BulkPostButton
          rows={rows}
          permission="sales.approve"
          reasonKey="perm.cannot.postDeliveries"
          invalidate={[DELIVERIES_KEY, ORDERS_KEY]}
          clear={clear}
          post={async (row) => {
            const response = await api.deliveryNotes.post({ params: { deliveryId: row.id }, body: {} });
            if (response.status !== 200) throwApiError(response.status, response.body);
          }}
        />
        <BulkPdfButton requests={rows.map(pdfRequest)} />
        <BulkExportButton filename="delivery-notes" columns={exportColumns} rows={rows} />
      </>
    ),
    detail: {
      chip,
      links: (note) => [
        {
          key: 'order',
          to: `/sales/orders?selected=${note.salesOrderId}`,
          tone: 'muted',
          label: t('sales.fromOrder', { code: note.salesOrderCode }),
        },
      ],
      subline: (note) => (
        <>
          {note.partnerName} · {note.warehouseName} · <bdi dir="ltr">{note.deliveryDate}</bdi>
        </>
      ),
      steps: { labelKey: 'orders.stepper', of: deliverySteps },
      /* "delivered / required" — the note's own count against the order's. */
      progress: (note) => t('dn.listProgress', { delivered: trimQty(note.totalDelivered), ordered: trimQty(note.totalOrdered) }),
      actions: (note) => (
        <>
          <Button asChild variant="outline">
            <Link to={`/sales/deliveries/${note.id}`}>
              <Pencil aria-hidden /> {note.status === 'draft' ? t('common.edit') : t('doclist.open')}
            </Link>
          </Button>
          {note.status === 'draft' ? (
            <Gated permission="sales.approve" reasonKey="perm.cannot.postDeliveries">
              <Button onClick={() => void act(() => postDelivery.mutateAsync({}))} disabled={busy}>
                <CheckCheck aria-hidden /> {t('dn.actPost')}
              </Button>
            </Gated>
          ) : null}
          <DocumentPdfButton request={pdfRequest(note)} />
          {note.status === 'posted' ? (
            <Gated permission="sales.create" reasonKey="perm.cannot.createInvoices">
              <Button asChild variant="outline">
                <Link to={`/sales/orders?selected=${note.salesOrderId}`}>
                  <Receipt aria-hidden /> {t('dn.actInvoice')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          {note.status === 'posted' ? (
            <Gated permission="sales.approve" reasonKey="perm.cannot.cancelDeliveries">
              <Button variant="destructive" onClick={() => setCancelAsk(true)} disabled={busy}>
                <XCircle aria-hidden /> {t('dn.actCancel')}
              </Button>
            </Gated>
          ) : null}
          {note.status === 'posted' ? (
            <ReasonBox
              open={cancelAsk}
              title={t('dn.listCancelTitle')}
              prompt={t('dn.listCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => {
                setCancelAsk(false);
                void act(() => cancelDelivery.mutateAsync(reason));
              }}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </>
      ),
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'warehouse', labelKey: 'dn.colWarehouse' },
          { key: 'ordered', labelKey: 'dn.colOrdered', align: 'end', className: 'w-28' },
          { key: 'qty', labelKey: 'dn.colDelivered', align: 'end', className: 'w-28' },
        ],
        rows: (note) =>
          note.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <span key="w">{line.warehouseName}</span>,
              <Qty key="o" value={line.qtyOrdered} unit={line.unitSymbol} precision={line.unitPrecision} />,
              <Qty key="q" value={line.qty} unit={line.unitSymbol} precision={line.unitPrecision} />,
            ],
          })),
      },
    },
  };

  return <DocumentListPage config={config} />;
}
